import { Stack, styled } from '@mui/material';

import { focusRing, hoverable, pressable, theme } from '@/shared/design-system';
import { typographyVariantStyles } from '@/shared/design-system/typography-variants';
import { forMobile } from '@/styles/responsive';

const AVATAR_SIZE = 32;
const AVATAR_COLUMN = AVATAR_SIZE + theme.spacing[12];

export const TabRoot = styled(Stack)({
  flexDirection: 'column',
  gap: theme.spacing[24],
  minWidth: 0,
});

export const SectionHeader = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[8],
});

export const ComposerCard = styled(Stack)({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
  minWidth: 0,
  padding: theme.spacing[16],
  borderRadius: theme.radius[12],
  border: `1px solid ${theme.colors.border.default}`,
  background: theme.colors.background.raised,
});

export const ComposerHeader = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[8],
});

export const ChipsRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  '& > *': { minWidth: 0, maxWidth: '100%' },
});

export const ComposerFooter = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[12],
  flexWrap: 'wrap',
  [forMobile(muiTheme)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const ComposerTools = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[12],
  minWidth: 0,
  flexWrap: 'wrap',
});

export const CharCounter = styled('span', {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean }>(({ error }) => ({
  ...typographyVariantStyles.body4,
  alignSelf: 'flex-end',
  color: error ? theme.colors.text.error : theme.colors.text.tertiary,
}));

export const AttachmentList = styled(Stack)({
  flexDirection: 'column',
  gap: theme.spacing[8],
});

export const AttachmentRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean }>(({ error }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[12],
  padding: `${theme.spacing[8]}px ${theme.spacing[12]}px`,
  borderRadius: theme.radius[8],
  border: `1px solid ${error ? theme.colors.error.border : theme.colors.border.subtle}`,
  background: theme.colors.background.default,
  minWidth: 0,
}));

export const AttachmentInfo = styled(Stack)({
  flexDirection: 'column',
  gap: theme.spacing[2],
  flex: 1,
  minWidth: 0,
});

export const AttachmentName = styled('span')({
  ...typographyVariantStyles.body3,
  fontWeight: 500,
  color: theme.colors.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const ToggleRow = styled(Stack)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[8],
  cursor: 'pointer',
  userSelect: 'none',
});

export const RefreshErrorRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  padding: `${theme.spacing[4]}px ${theme.spacing[8]}px`,
});

export const FeedList = styled(Stack)({
  flexDirection: 'column',
  gap: theme.spacing[12],
});

export const EntryCard = styled(Stack)({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  padding: theme.spacing[16],
  borderRadius: theme.radius[12],
  border: `1px solid ${theme.colors.border.subtle}`,
  background: theme.colors.background.raised,
  minWidth: 0,
});

export const EntryHeader = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing[12],
});

export const EntryByline = styled(Stack)({
  flexDirection: 'column',
  gap: theme.spacing[2],
  flex: 1,
  minWidth: 0,
});

export const EntryNameRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const EntryBody = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'column',
  gap: theme.spacing[8],
  paddingLeft: AVATAR_COLUMN,
  minWidth: 0,
  [forMobile(muiTheme)]: {
    paddingLeft: 0,
  },
}));

export const EntryContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'expanded' && prop !== 'muted',
})<{ expanded?: boolean; muted?: boolean }>(({ expanded, muted }) => ({
  ...typographyVariantStyles.body3,
  color: muted ? theme.colors.text.secondary : theme.colors.text.primary,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  ...(expanded
    ? {}
    : {
        display: '-webkit-box',
        WebkitLineClamp: 6,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }),
}));

export const ShowMoreButton = styled('button')({
  alignSelf: 'flex-start',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: theme.colors.text.brand,
  fontFamily: 'inherit',
  ...typographyVariantStyles.body3,
  ...hoverable(theme, 'brandText'),
  ...pressable(theme, 'brandText'),
  '&:disabled': {
    cursor: 'default',
    color: theme.colors.text.tertiary,
  },
});

export const SystemRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing[12],
  padding: `${theme.spacing[4]}px ${theme.spacing[16]}px`,
  color: theme.colors.text.secondary,
  minWidth: 0,
});

export const SystemIconSlot = styled('span')({
  display: 'inline-flex',
  justifyContent: 'center',
  flexShrink: 0,
  width: AVATAR_SIZE,
  paddingTop: 2,
  color: theme.colors.icon.tertiary,
});

export const SystemText = styled('div')({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.secondary,
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
});

export const InlineMeta = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[4],
});

export const GroupDetails = styled('ul')({
  margin: 0,
  paddingLeft: theme.spacing[16],
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  ...typographyVariantStyles.body4,
  color: theme.colors.text.secondary,
});

export const ValueMark = styled('span')({
  color: theme.colors.text.primary,
  fontWeight: 500,
});

export const AttachmentChips = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
});

export const AttachmentImages = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
  maxWidth: '100%',
});

export const AttachmentImageButton = styled('button')({
  boxSizing: 'border-box',
  display: 'block',
  flex: '0 0 min(120px, 100%)',
  width: 120,
  maxWidth: '100%',
  aspectRatio: '1',
  minWidth: 0,
  padding: 0,
  overflow: 'hidden',
  border: `1px solid ${theme.colors.border.subtle}`,
  borderRadius: theme.radius[8],
  background: theme.colors.background.default,
  cursor: 'pointer',
  appearance: 'none',
  ...hoverable(theme, 'lift'),
  ...focusRing(theme),
});

export const AttachmentImage = styled('img')({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const AttachmentChip = styled('div')({
  display: 'inline-flex',
  alignItems: 'stretch',
  maxWidth: '100%',
  borderRadius: theme.radius[8],
  border: `1px solid ${theme.colors.border.subtle}`,
  background: theme.colors.background.default,
  overflow: 'hidden',
  '& svg': { flexShrink: 0, width: 16, height: 16 },
});

const chipButtonReset = {
  margin: 0,
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  ...typographyVariantStyles.body4,
} as const;

export const AttachmentChipMain = styled('button')({
  ...chipButtonReset,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  minWidth: 0,
  padding: `${theme.spacing[4]}px ${theme.spacing[8]}px`,
  color: theme.colors.text.primary,
  textAlign: 'left',
  'button&': {
    cursor: 'pointer',
    '&:hover': { background: theme.colors.background.raised },
  },
});

export const AttachmentChipAction = styled('button')({
  ...chipButtonReset,
  display: 'inline-flex',
  alignItems: 'center',
  padding: `0 ${theme.spacing[8]}px`,
  borderLeft: `1px solid ${theme.colors.border.subtle}`,
  color: theme.colors.icon.tertiary,
  cursor: 'pointer',
  '&:hover': {
    color: theme.colors.text.primary,
    background: theme.colors.background.raised,
  },
});

export const AttachmentChipName = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const LoadMoreRow = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'center',
  paddingTop: theme.spacing[8],
});

export const QuietLine = styled(Stack)({
  paddingTop: theme.spacing[16],
  paddingBottom: theme.spacing[16],
  alignItems: 'center',
  textAlign: 'center',
});

export const SkeletonBar = styled('div')<{ w?: string }>(({ w }) => ({
  height: theme.spacing[12],
  width: w ?? '100%',
  borderRadius: theme.radius[4],
  background: theme.colors.background.overlay,
  animation: 'gpHistoryPulse 1.4s ease-in-out infinite',
  '@keyframes gpHistoryPulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 1 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));
